import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Modal, Button, Input, InputNumber, Form, Space, Select, Flex, Tooltip, Divider, Tag, notification } from 'antd';
import { UndoOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { v4 as uuid } from 'uuid';

import FormHeader from '@controls/form-header/form-header';
import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { serverFetch } from '@src/server';

import { ICity } from '@entities/city';
import { ICountry } from '@entities/country';
import { IUserSession } from '@src/entities/user-session';
import { ITariffLine } from '@entities/tariff-line';
import { ITariff } from '@entities/tariff';
import { IConsignee } from '@entities/consignee';
import { ICargoCarrier } from '@entities/cargo-carrier';
import { ICountryItem } from '@entities/country-item';

import { Permission, hasPermission } from '@src/enums/permission';

import { TruckIcon, AirplaneIcon } from '@icons/index';

const Tariff = () => {
    const { TextArea } = Input;
    const [form] = Form.useForm();

    const { headerId, id, isCopy } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<ITariff>({
        id: undefined,
        headerId: headerId,
        userId: undefined,
        cityId: undefined,
        lines: [],
    });

    const [consignees, setConsignees] = useState<Array<IConsignee>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [canManage] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageTariff));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries/items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cargocarriers/air', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения авиаперевозчиков', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('consignees', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`tariffs/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения тарифа', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const countries = result[0][0] as Array<ICountry>;
                const items = result[0][1] as Array<ICountryItem>;
                const cargoCarriers = result[0][2] as Array<ICargoCarrier>;

                setConsignees(result[0][3]);
                setCities(result[0][4]);

                let tmpTariff: ITariff;

                let lines: Array<ITariffLine> = [];

                if (id) {
                    tmpTariff = result[0][6] as ITariff;

                    if (isCopy) {
                        tmpTariff.id = undefined;
                        tmpTariff.userId = undefined;
                    }

                    lines = tmpTariff.lines.filter((l) => {
                        const country = countries.find((c) => c.id === l.countryId);
                        if (country) {
                            l.countryName = country.name;
                            return l;
                        }
                    });

                    lines.map((l) => {
                        if (isCopy) {
                            l.id = undefined;

                            l.details.map((d) => {
                                d.id = undefined;
                            });
                        }
                    });
                } else {
                    tmpTariff = entity;

                    countries.map((c) => {
                        const line: ITariffLine = {
                            id: undefined,
                            tariffId: undefined,
                            countryId: c.id,
                            countryName: c.name,
                            details: [],
                        };

                        lines.push(line);
                    });
                }

                lines.map((l) => {
                    const countryItems = items.filter((e) => e.countryId == l.countryId);
                    const countryCargoes = cargoCarriers.filter((e) => e.countryId == l.countryId);

                    countryItems.map((i) => {
                        const detail = l.details.find((d) => d.itemId == i.itemId);

                        if (!detail) {
                            countryCargoes.map((c) => {
                                l.details.push({
                                    id: undefined,
                                    tariffLineId: l.id,
                                    itemId: i.itemId,
                                    unitType: i.unitType,
                                    handlingPrice: c.price,
                                    precoolingPrice: undefined,
                                    cargoCarrierId: c.id,
                                    cargoCarrierName: c.name,
                                    itemName: i.itemName,
                                });
                            });

                            l.details.push({
                                id: undefined,
                                tariffLineId: l.id,
                                itemId: i.itemId,
                                unitType: i.unitType,
                                handlingPrice: undefined,
                                precoolingPrice: undefined,
                                itemName: i.itemName,
                            });
                        }
                    });
                });

                setEntity({ ...tmpTariff, lines: lines });

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const resetDefault = () => {
        setLoading(true);

        serverFetch(`tariffs/${id}/default`, { method: 'GET' })
            .then((result: Array<ITariffLine>) => {
                setLoading(false);

                const lines = [...entity.lines];

                for (let i = 0; i < result.length; i++) {
                    const defaultLine = result[i];

                    const line = lines.find((l) => l.countryId === defaultLine.countryId);
                    if (line) {
                        line.details.map((d) => {
                            const defaultDetail = defaultLine?.details.find((e) => e.id == d.id);
                            d.handlingPrice = defaultDetail?.handlingPrice;
                            d.precoolingPrice = defaultDetail?.precoolingPrice;

                            form.setFieldValue(`handlingPrice${line?.id}${d.itemId}${d.cargoCarrierId}`, defaultDetail?.handlingPrice);
                            form.setFieldValue(`precoolingPrice${line?.id}${d.itemId}${d.cargoCarrierId}`, defaultDetail?.precoolingPrice);
                        });
                    }
                }

                setEntity({ ...entity, lines: lines });
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка получения тарифа по умолчанию', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSave = () => {
        setLoading(true);

        const data = { ...entity };

        data.lines.map((l) => {
            l.details.map((d) => {
                d.handlingPrice = form.getFieldValue(`handlingPrice${l.id}${d.itemId}${d.cargoCarrierId}`);
                d.precoolingPrice = form.getFieldValue(`precoolingPrice${l.id}${d.itemId}${d.cargoCarrierId}`);
            });
        });

        serverFetch(`tariffs`, { method: id && !isCopy ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onConsigneeChanged = (id: string) => {
        const consignee = consignees.find((m) => m.id === id);

        const city = cities.find((c) => c.id === consignee?.cityId);

        const data = { ...entity };
        data.userId = id;

        data.lines = data.lines.map((l) => {
            l.details.map((d) => {
                form.setFieldValue(`handlingPrice${l.id}${d.itemId}${d.cargoCarrierId}`, d.handlingPrice);
            });

            return l;
        });
    };

    const renderLines = () => {
        return entity.lines.map((l) => {
            return (
                <div key={l.id}>
                    <Form.Item>
                        <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                            {l.countryName}
                        </Divider>
                    </Form.Item>
                    <>
                        {l.details.map((d) => {
                            return (
                                <Form.Item
                                    key={uuid()}
                                    labelAlign='left'
                                    label={d.itemName}
                                    style={{ marginBottom: 0 }}
                                    labelCol={{ span: 6 }}
                                    wrapperCol={{ span: 18 }}
                                >
                                    <Flex gap='small'>
                                        <Form.Item style={{ width: 200 }}>
                                            <Flex justify='flex-end'>
                                                <Tag color={d.cargoCarrierId ? 'var(--cargo-color)' : ''}>
                                                    {d.cargoCarrierName || 'Базовая доставка'}
                                                </Tag>
                                            </Flex>
                                        </Form.Item>
                                        <Form.Item
                                            initialValue={d.precoolingPrice}
                                            name={`truckPrice${l.id}${d.itemId}${d.cargoCarrierId}`}
                                        >
                                            <Space.Compact>
                                                <InputNumber decimalSeparator=',' min={0} />
                                                <Space.Addon>
                                                    <Tooltip
                                                        title={
                                                            d.cargoCarrierId
                                                                ? 'Транспортная доставка с перелетом'
                                                                : 'Транспортная доставка '
                                                        }
                                                        placement='topRight'
                                                    >
                                                        <div>
                                                            <TruckIcon style={{ color: d.cargoCarrierId ? 'var(--cargo-color)' : '' }} />
                                                        </div>
                                                    </Tooltip>
                                                </Space.Addon>
                                            </Space.Compact>
                                        </Form.Item>
                                        <Form.Item
                                            initialValue={d.handlingPrice}
                                            name={`handlingPrice${l.id}${d.itemId}${d.cargoCarrierId}`}
                                            style={{ visibility: d.cargoCarrierId ? 'visible' : 'hidden' }}
                                        >
                                            <Space.Compact>
                                                <InputNumber decimalSeparator=',' min={0} step='0.1' stringMode disabled={!canManage} />
                                                <Space.Addon>
                                                    <Tooltip title='Перелет' placement='topRight'>
                                                        <div>
                                                            <AirplaneIcon />
                                                        </div>
                                                    </Tooltip>
                                                </Space.Addon>
                                            </Space.Compact>
                                        </Form.Item>
                                    </Flex>
                                </Form.Item>
                            );
                        })}
                    </>
                </div>
            );
        });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                style={{ padding: '10px 0 0' }}
                commands={[
                    {
                        type: 'primary',
                        label: 'Сбросить тариф по умолчанию',
                        key: 'resetDefault',
                        icon: <UndoOutlined />,
                        onClick: () => {
                            /*   if (!hasPermission(userSession.permissions, Permission.)) {
                                securityRestriction(api, [Permission.]);
                                return;
                            } */

                            modal.confirm({
                                title: `Сбросить значения тарифа по умолчанию?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    resetDefault();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    return (
        <>
            <FormHeader title={`${id && !isCopy ? (!canManage ? 'Информация о тарифе' : 'Изменить тариф') : 'Добавить тариф'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 9 }} onFinish={onSave} form={form}>
                    <Form.Item
                        initialValue={entity.consigneeCode}
                        required
                        label='Код клиента'
                        name='userId'
                        rules={[{ required: true, message: 'Выберите клиента' }]}
                    >
                        <Select
                            autoFocus
                            disabled={!canManage}
                            onChange={(value) => {
                                onConsigneeChanged(value);
                            }}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => {
                                    if ((a?.label as string).length !== (b?.label as string).length) {
                                        return (a?.label as string).length - (b?.label as string).length;
                                    }

                                    return (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase());
                                },
                            }}
                            options={consignees.map((c) => {
                                return { value: c.id, label: `${c.code}` };
                            })}
                        ></Select>
                    </Form.Item>
                    <Form.Item label='Комментарий' name='comment' initialValue={entity?.comment}>
                        <TextArea
                            disabled={!canManage}
                            rows={4}
                            onChange={(data) => {
                                setEntity({ ...entity, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 11 }} style={{ marginBottom: 0 }}>
                        {renderToolbar()}
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 11 }}>{entity.lines && renderLines()}</Form.Item>

                    <Form.Item label='' wrapperCol={{ offset: 3, span: 9 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {canManage && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Tariff;
